import { addDays, differenceInMonths, format, intervalToDuration } from 'date-fns';
import cloneDeep from 'lodash/cloneDeep';
import React from 'react';
import { UseFormSetValue } from 'react-hook-form';
import { EmployeeForm, Employment, EmploymentForm } from 'types/employees';
import { JobPositionDto } from 'types/jobPositions';
import { JobRolesListElement } from 'types/jobRoles';
import { formatDateStringInput } from 'utils/formatDateStringInput';
import { DEFAULT_DATE_FORMAT } from 'utils/parseDate';

export const mapEmploymentDtoToForm = (dto: Employment): EmploymentForm => ({
    recordId: dto.recordId,
    jobRole: dto.jobRoleId !== null && dto.jobRoleName !== null ? { id: dto.jobRoleId, name: dto.jobRoleName } : null,
    jobPosition:
        dto.jobPositionId !== null && dto.jobPositionName !== null
            ? { id: dto.jobPositionId, name: dto.jobPositionName }
            : null,
    office: dto.officeId !== null && dto.officeName !== null ? { id: dto.officeId, name: dto.officeName } : null,
    coreTeam:
        dto.coreTeamId !== null && dto.coreTeamName !== null ? { id: dto.coreTeamId, name: dto.coreTeamName } : null,
    validFrom: dto.validFrom ? formatDateStringInput(new Date(dto.validFrom)) : null,
    validTo: dto.validTo ? formatDateStringInput(new Date(dto.validTo)) : null,
    error: false,
});

export const getPracticeAreas = (
    practiceAreas: {
        id: number;
        name: string;
    }[]
) => {
    if (!practiceAreas?.length) return null;
    const areas: React.ReactNode[] = [];

    (practiceAreas || []).forEach((item, index) => {
        const optionalComma = index < (practiceAreas || []).length - 1 ? `,\u00A0` : null;
        areas.push(
            <span key={item.id} className="text-body1">
                {item.name}
                {optionalComma}
            </span>
        );
    });

    return <div className="flex flex-wrap">{areas}</div>;
};

export const mapJobRolesToNamedEntities = (jobRoles: JobRolesListElement[] | undefined) => {
    return jobRoles
        ? jobRoles.map((jobRole) => {
              return { id: jobRole.id, name: jobRole.internalName };
          })
        : [];
};

export const mapJobPositionDtosToNamedEntities = (jobPositions: JobPositionDto[] | undefined) => {
    return jobPositions
        ? jobPositions.map((jobPosition) => {
              return { id: jobPosition.id, name: jobPosition.name };
          })
        : [];
};

export const findSmallestRecordId = (list: EmploymentForm[]) => {
    if (!Array.isArray(list) || list.length === 0) {
        return 0;
    }
    return list.reduce((min, record) => (record.recordId < min ? record.recordId : min), 0);
};

export const sortEmploymentHistoryByValidFrom = (
    employmentHistory: EmploymentForm[],
    order: 'asc' | 'desc' = 'desc'
) => {
    return employmentHistory.sort((a, b) => {
        if (a.validFrom === null) return order === 'desc' ? 1 : -1;
        if (b.validFrom === null) return order === 'desc' ? -1 : 1;
        const bTime = new Date(b.validFrom).getTime();
        const aTime = new Date(a.validFrom).getTime();
        return order === 'desc' ? bTime - aTime : aTime - bTime;
    });
};

export const getEmploymentGapLabel = (
    earlierValidTo: string | null | undefined,
    laterValidFrom: string | null | undefined
): string | null => {
    if (!earlierValidTo || !laterValidFrom) {
        return null;
    }

    const gapStart = new Date(earlierValidTo);
    const gapEnd = new Date(laterValidFrom);
    if (gapEnd <= gapStart || differenceInMonths(gapEnd, gapStart) < 1) {
        return null;
    }

    const { years = 0, months = 0 } = intervalToDuration({ start: gapStart, end: gapEnd });
    const parts: string[] = [];
    if (years > 0) {
        parts.push(`${years} year${years > 1 ? 's' : ''}`);
    }
    if (months > 0) {
        parts.push(`${months} month${months > 1 ? 's' : ''}`);
    }

    return parts.length ? `${parts.join(' ')} career break` : null;
};

export const calculateInitialValidFrom = (employmentHistory: EmploymentForm[]) => {
    if (!employmentHistory.length) {
        return formatDateStringInput(new Date());
    }

    const employmentHistoryCopy = cloneDeep(employmentHistory ?? []);
    sortEmploymentHistoryByValidFrom(employmentHistoryCopy);
    const latestEmployment = employmentHistoryCopy[0];
    const nextEmploymentDate = latestEmployment?.validTo ? addDays(new Date(latestEmployment.validTo), 1) : new Date();

    return format(nextEmploymentDate, DEFAULT_DATE_FORMAT);
};

export const isTimeIntervalOverlapping = (employmentHistory: EmploymentForm[]): boolean => {
    if (!employmentHistory.length || employmentHistory.length === 1) {
        return false;
    }

    const employmentHistoryState = cloneDeep(employmentHistory ?? []);
    sortEmploymentHistoryByValidFrom(employmentHistoryState, 'asc');

    for (let recordIndex = 0; recordIndex < employmentHistoryState.length; recordIndex++) {
        const employment = employmentHistoryState[recordIndex];
        const hasNext = recordIndex < employmentHistoryState.length - 1;

        if (hasNext && !employment.validTo) {
            return true;
        } else if (
            hasNext &&
            employment.validTo &&
            employmentHistoryState[recordIndex + 1].validFrom &&
            new Date(employment.validTo) >= new Date(employmentHistoryState[recordIndex + 1].validFrom!)
        ) {
            return true;
        }
    }

    return false;
};

export const validateEmploymentHistory = (
    employeeForm: EmployeeForm,
    setValue: UseFormSetValue<EmployeeForm>
): boolean => {
    const employmentHistoryState = cloneDeep(employeeForm.employmentHistory ?? []);

    if (!employmentHistoryState.length) {
        return false;
    }

    const validatedEmployments = (employmentHistoryState as EmploymentForm[]).map((item) => ({
        ...item,
        error: !item.jobRole?.id || !item.validFrom || !item.jobPosition?.id || !item.office?.id || !item.coreTeam?.id,
    }));

    setValue('employmentHistory', validatedEmployments);
    return !validatedEmployments.find((item) => item?.error);
};
